import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiPaginationLinks, ApiPaginationMeta } from '@frontend/shared';
import { Observable, TimeoutError, forkJoin, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { InsightsService } from '../content';
import { InsightExtended } from '../contributions/insight-extended.model';
import { PaginatedInsightsResponse } from '../contributions/insight-response.model';
import { Culture, CultureService } from '../cultures';
import { Difference, DifferenceService } from '../differences';
import { ErrorService, NormalisedHttpError } from '../error';
import { SendInsightForTranslationRequest, SendInsightForTranslationResponse } from './insight-translation.model';

export interface EditorialInsightsApiQuery {
  cultures: string[];
  differences?: string[];
  category?: string[];
  type?: string[];
  exclude_differences?: string[];
  per_page?: number;
  page?: number;
}

/**
 * The `GET api/v1/insights` envelope after transformation — the same shape as
 * `PaginatedInsightsResponse`, with each item normalised into an `InsightExtended`.
 */
export interface PaginatedEditorialInsights {
  data: InsightExtended[];
  links: ApiPaginationLinks;
  meta: ApiPaginationMeta;
}

@Injectable({
  providedIn: 'root',
})
export class EditorialService {
  constructor(
    private http: HttpClient,
    private cultureService: CultureService,
    private differenceService: DifferenceService,
    private insightsService: InsightsService,
    private errorService: ErrorService,
  ) {}

  getReviewSeedData(differenceLimit: number = 12): Observable<{ cultures: Culture[]; differences: Difference[] }> {
    return forkJoin({
      cultures: this.getCulturesForReview(),
      differences: this.getCcoDifferencesForReview(differenceLimit),
    });
  }

  getCulturesForReview(): Observable<Culture[]> {
    return this.cultureService.getCultures('geographic', 'national', false).pipe(
      map((cultures) => [...(cultures ?? [])].sort((a, b) => a.name.localeCompare(b.name))),
    );
  }

  getCcoDifferencesForReview(limit: number = 12): Observable<Difference[]> {
    return this.differenceService.getDifferences('cco', null).pipe(
      map((differences) =>
        [...(differences ?? [])]
          .filter((difference) => difference?.category === 'cco')
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, limit),
      ),
    );
  }

  getInsightsForReview(query: EditorialInsightsApiQuery): Observable<PaginatedEditorialInsights> {
    let params = new HttpParams();

    (query.cultures ?? []).forEach((cultureSlug) => {
      params = params.append('cultures[]', cultureSlug);
    });

    (query.differences ?? []).forEach((differenceSlug) => {
      params = params.append('differences[]', differenceSlug);
    });

    (query.exclude_differences ?? []).forEach((differenceSlug) => {
      params = params.append('exclude_differences[]', differenceSlug);
    });

    if (query.category !== undefined) {
      if (!query.category.length) {
        params = params.append('category[]', '');
      } else {
        query.category.forEach((category) => {
          params = params.append('category[]', category);
        });
      }
    }

    if (query.type !== undefined) {
      if (!query.type.length) {
        params = params.append('type[]', '');
      } else {
        query.type.forEach((type) => {
          params = params.append('type[]', type);
        });
      }
    }

    if (query.per_page) {
      params = params.set('per_page', query.per_page.toString());
    }

    if (query.page && query.page > 0) {
      params = params.set('page', query.page.toString());
    }

    return this.http.get<PaginatedInsightsResponse>('api/v1/insights', { params }).pipe(
      map((response) => {
        const transformedInsights: InsightExtended[] = (response?.data ?? []).map((insightFromBackend) =>
          this.insightsService.transformInsight(insightFromBackend),
        );

        return {
          data: transformedInsights,
          links: response?.links,
          meta: response?.meta,
        };
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  /**
   * Translates one insight across fields × target languages in a single call — see
   * `insight-translation.model.ts`. There is no strategy to pass: the backend derives how
   * each field travels from the format its model declares for that field.
   *
   * The backend makes one provider round-trip per combination, sequentially, so a
   * 2-field × 3-language run is 6 calls and can take minutes. Hence the generous timeout;
   * there is no polling or job-status API to fall back on. Callers should show real
   * progress, and must not fire this repeatedly (all `api/*` routes share a
   * 60 requests/minute bucket).
   *
   * The backend defaults `dry_run` to `false`, i.e. it **writes to the insight**. This
   * method therefore defaults it to `true` — pass `dry_run: false` only when the user has
   * explicitly asked to save.
   */
  sendInsightForTranslation(
    insightId: number,
    request: SendInsightForTranslationRequest,
  ): Observable<SendInsightForTranslationResponse> {
    const payload: SendInsightForTranslationRequest = { dry_run: true, ...request };

    return this.http
      .post<SendInsightForTranslationResponse>(`api/v1/insights/${insightId}/send-for-translation`, payload)
      .pipe(
        timeout(EditorialService.TRANSLATION_RUN_TIMEOUT_MS),
        catchError((error) => throwError(() => this.normaliseTranslationError(error))),
      );
  }

  /** Generous: a multi-field, multi-language run is a sequence of provider round-trips. */
  private static readonly TRANSLATION_RUN_TIMEOUT_MS = 300000;

  private normaliseTranslationError(error: unknown): NormalisedHttpError {
    if (error instanceof TimeoutError) {
      return { message: 'The translation run took too long and was abandoned. Try fewer languages or fields.' };
    }

    if (error instanceof HttpErrorResponse) {
      return this.errorService.normaliseHttpError(error, 'error.something_went_wrong');
    }

    return { message: 'error.something_went_wrong' };
  }

  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';

    if (!errorResponse?.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    }

    const standardErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(errorResponse.error.message);
    if (standardErrorMessageTranslationKey) {
      errorMessage = standardErrorMessageTranslationKey;
    }

    if (errorResponse.error.meta) {
      return throwError({ message: errorMessage, meta: errorResponse.error.meta });
    }

    return throwError(errorMessage);
  }
}
